import Alpine from './alpine.esm.js';
import './cart-actions.js';

window.Alpine = Alpine;

/* Shared mobile-nav state — lives in a store (not the header component)
   so the off-canvas drawer can render as a top-level sibling OUTSIDE the
   header. Nested inside <header>, the homepage header's `backdrop-filter`
   makes it a containing block for the fixed drawer and clips it to the
   72px header box; as a sibling the drawer is reliably viewport-fixed. */
Alpine.store('mobileNav', {
  open: false,
  shopOpen: false,

  toggle() {
    if (this.open) this.closeNav();
    else this.openNav();
  },

  openNav() {
    /* Offset the drawer below the sticky header (marquee + bar) so both
       stay visible above it. Read the live header bottom so it tracks the
       homepage's transparent→scrolled morph and the in-flow marquee. */
    const header = document.querySelector('.site-header');
    const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    document.documentElement.style.setProperty('--mobile-nav-top', top + 'px');
    this.open = true;
    document.documentElement.classList.add('mobile-nav-open');
    requestAnimationFrame(() => {
      const close = document.querySelector('.mobile-nav__close');
      if (close) close.focus();
    });
  },

  closeNav() {
    this.open = false;
    this.shopOpen = false;
    document.documentElement.classList.remove('mobile-nav-open');
    document.documentElement.style.removeProperty('--mobile-nav-top');
  },
});

/* Quick Look modal for the bundle builder's flavor cards — shows a
   flavor's PDP description + accordion drawers (nutrition, ingredients,
   merchant info drawers) without leaving Build Your Box. Rendered inside
   the bundleBuilder x-data root (see snippets/quick-look-modal.liquid,
   teleported to <body> from sections/bundle-builder.liquid) so its
   template can call addItem/removeItem/canAdd/getItemQuantity directly.

   The accordion markup itself isn't duplicated here: it's fetched live
   from the flavor's own product page and its .pdp-info-accordion node is
   spliced into the modal, then re-scanned with Alpine.initTree so its
   own local x-data("{ open: 'description' }") comes alive. That accordion
   CSS already ships globally (every section's {% stylesheet %} compiles
   into one sitewide bundle — see AGENTS.md), so no styles need copying.

   NOTE: `_render()` is called imperatively (from `show()` and from the
   mount point's one-shot x-init) and must NEVER be moved into an
   `x-effect`. Alpine registers effects created during a running effect
   as children of it and releases them when it re-runs — so an
   initTree() inside x-effect silently strips the spliced accordion's
   own reactivity. Its `open` state still toggled on click, but x-show
   and :aria-expanded stopped re-rendering, leaving every drawer stuck. */
Alpine.store('quickLook', {
  open: false,
  product: null,
  list: [],
  loading: false,
  error: false,
  _el: null,
  _html: '',
  _renderedHTML: null,
  _lastHandle: null,
  _touchX: 0,
  _touchY: 0,

  // One-shot x-init on the mount point — captures the node and renders
  // whatever is already fetched. This is also what makes it safe for
  // show() to call _render() without awaiting anything: if the mount
  // point hasn't been created yet, this picks the splice up on arrival.
  bind(el) {
    this._el = el;
    this._render();
  },

  async show(product, list) {
    if (Array.isArray(list) && list.length) this.list = list;
    this.open = true;
    this.product = product;
    document.documentElement.classList.add('quick-look-locked');

    if (product.handle === this._lastHandle) return; // already fetched this flavor
    this._lastHandle = product.handle;
    this.loading = true;
    this.error = false;
    this._html = ''; // drop the previous flavor's drawers so they don't flash while this one loads
    this._render();
    try {
      const res = await fetch(`/products/${product.handle}`);
      if (!res.ok) throw new Error('Quick Look fetch failed');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const accordion = doc.querySelector('.pdp-info-accordion');
      this._html = accordion ? accordion.outerHTML : '';
    } catch {
      this.error = true;
      this._lastHandle = null; // allow a retry on next open
    } finally {
      this.loading = false;
    }
    /* Deliberately NOT `await Alpine.nextTick()` here. Alpine can hold its
       tick queue (holdNextTicks during cloning), so that promise is not
       guaranteed to resolve — when it didn't, the fetched accordion was
       never spliced in and every drawer rendered empty. bind() covers the
       case where the mount point appears after this runs. */
    this._render();
  },

  // Step to the previous (-1) / next (+1) flavor in the grid's current
  // order — desktop arrows, mobile swipe, and left/right arrow keys.
  step(dir) {
    if (!this.open || !this.product || this.list.length < 2) return;
    const i = this.list.findIndex((p) => p.id === this.product.id);
    if (i === -1) return;
    const next = this.list[(i + dir + this.list.length) % this.list.length];
    if (next) this.show(next, this.list);
  },

  touchStart(e) {
    const t = e.changedTouches[0];
    this._touchX = t.clientX;
    this._touchY = t.clientY;
  },

  // Horizontal-dominant flicks only, so vertical scrolling through the
  // drawers never gets read as a flavor change.
  touchEnd(e) {
    const t = e.changedTouches[0];
    const dx = t.clientX - this._touchX;
    const dy = t.clientY - this._touchY;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.step(dx < 0 ? 1 : -1);
  },

  close() {
    this.open = false;
    document.documentElement.classList.remove('quick-look-locked');
  },

  _render() {
    const el = this._el;
    if (!el || this._renderedHTML === this._html) return;
    this._renderedHTML = this._html;
    el.innerHTML = '';
    if (!this._html) return;

    /* Sanitize inside an inert <template> BEFORE the markup reaches the
       live document — Alpine's MutationObserver auto-initializes any
       x-data it sees get inserted, so stripping after insertion would be
       too late. */
    const tpl = document.createElement('template');
    tpl.innerHTML = this._html;
    const accordion = tpl.content.querySelector('.pdp-info-accordion');
    if (!accordion) return;
    this._takeOverDrawers(accordion);
    el.appendChild(accordion);
  },

  /* The fetched accordion arrives carrying the PDP's own Alpine wiring
     (x-data / x-show / x-transition / @click / :aria-expanded). Cloning
     and that wiring is stripped rather than reused, for two reasons:

       1. Cloning it into this already-running Alpine tree breaks it:
          effects created by initTree get released by whatever effect is
          on the stack, so `open` toggles but nothing re-renders. This is
          the blocking one.
       2. It also drops the dependency on x-transition, whose enter
          stalls at `display: none; opacity: 0` whenever rAF is paused
          (a hidden or backgrounded tab), which would leave a drawer
          stuck invisible while its state says open.

     So the drawers are driven with plain DOM here, with visibility
     handled by CSS in sections/bundle-builder.liquid. Markup and class
     names are left untouched, so the PDP's styling still applies. */
  _takeOverDrawers(accordion) {
    const strip = (node) => {
      [...node.attributes].forEach(({ name }) => {
        if (name.startsWith('x-') || name.startsWith('@') || name.startsWith(':')) {
          node.removeAttribute(name);
        }
      });
    };
    strip(accordion);
    accordion.querySelectorAll('*').forEach(strip);

    const setOpen = (item, isOpen) => {
      item.classList.toggle('is-open', isOpen);
      const q = item.querySelector('.pdp-info-accordion__q');
      if (q) q.setAttribute('aria-expanded', String(isOpen));
    };

    // Matches the PDP: single-open, Description expanded to start.
    const items = [...accordion.querySelectorAll('.pdp-info-accordion__item')];
    items.forEach((item, i) => setOpen(item, i === 0));

    accordion.addEventListener('click', (e) => {
      const q = e.target.closest('.pdp-info-accordion__q');
      if (!q || !accordion.contains(q)) return;
      const item = q.closest('.pdp-info-accordion__item');
      const wasOpen = item.classList.contains('is-open');
      accordion
        .querySelectorAll('.pdp-info-accordion__item')
        .forEach((other) => setOpen(other, false));
      if (!wasOpen) setOpen(item, true);
    });
  },
});

/* ---- Fixed bottom bars → --bottom-bar-h ---- */
/* Publishes the height of whatever fixed bar is currently pinned to the
   bottom of the viewport (bundle builder mobile drawer, PDP buy bar) as
   a root custom property. brand-foundation.css feeds it into the Shopify
   Inbox activator's offset so the chat bubble rides above the bar and
   keeps rising as the bundle drawer expands. Height is measured as the
   slice of the bar overlapping the viewport bottom, so an expanding
   drawer keeps pushing the bubble up (capped below the sticky header). */
const BOTTOM_BAR_SELECTOR = '.builder-mobile, .pdp__sticky-bar';

/* ---- Chat activator: tuck aside when it covers a buy action ---- */
/* The lift above only dodges FIXED bars. Buttons in normal flow scroll
   straight through the bubble's bottom-right corner, and because the app
   paints at z-index 2147483000 the bubble always wins — a tap there hits
   <shopify-chat>, not the button. Measured on live at 390x844 (2026-08-24):
   the widget's 84x46 hit area sat 70px inside the PDP "Add to cart" and
   elementFromPoint returned SHOPIFY-CHAT. Same shape on build-your-box,
   where the right-hand "Add to box" column shares the bubble's lane — and
   the bar lift does not help, it only moves which scroll offset collides.
   So when a buy action lands under the bubble, slide the whole widget off
   the right edge and leave a theme-owned arrow tab to pull it back.

   Why the widget's own hooks aren't enough: the only exported part is
   ::part(activator), the visible pill. Its hit area is an outer
   .activator-nudge wrapper that is NOT a part, so moving just the pill
   leaves an invisible tap trap over the button — worse than the bug. The
   wrapper ignores `transform` even with !important; `right` works. That
   needs a stylesheet adopted into the (open) shadow root, which is
   unsupported by definition: selfTest() below proves the tuck actually
   moves the widget before the arrow tab is ever shown, so an Inbox class
   rename degrades to today's behaviour instead of a stranded arrow. */
const KEEP_CLEAR_SELECTOR = [
  '.pdp__cta', // PDP inline Add to cart
  '.flavor-card__add', // bundle builder "Add to box"
  '.box-cta', // box summary + mobile drawer "Add box to cart"
  '.cart__checkout-button', // cart page + cart drawer
  /* The fixed bars' own controls. These are supposed to be handled by the
     vertical lift above, not here — but the lift is not reaching the widget
     on real iOS: Joe's iPhone (Brave, 2026-08-24) shows the bubble ~23px off
     the bottom with the PDP buy bar up, i.e. its unlifted position, and
     build-your-box does the same with `--bottom-bar-h` correctly published
     as 57px. So the tuck doubles as the safety net. This composes with the
     lift rather than fighting it: where the lift works the bubble sits above
     the bar, never intersects these, and nothing tucks. */
  '.pdp__sticky-cta',
  '.builder-mobile__toggle',
].join(', ');
/* Near-misses read as collisions — a 2px gap is still a mis-tap. */
const KEEP_CLEAR_MARGIN = 6;
/* Breathing room between the bubble and the cart panel's left edge. */
const CART_CLEAR_GAP = 20;
/* Below this much free space to the left of the panel there is nowhere to
   shift into — the drawer is full-bleed (mobile), so fall back to the
   off-screen tuck. 84px pill + two gaps. */
const CART_MIN_CLEAR = 124;
/* How far the reader has to scroll before a manual pull-out lapses and
   auto-tuck resumes. Keeps chat out while they're reading this screen. */
const MANUAL_OUT_SCROLL = 160;

const chatTuck = {
  support: 'untested', // 'untested' | 'ok' | 'unsupported'
  tucked: false,
  manualOut: false,
  manualAt: 0,
  restInset: null, // px from the viewport's right edge when at rest
  tab: null,
  shifted: false, // sitting beside the cart panel rather than off-screen
  shiftRight: null, // the `right` inset that shift is currently using
};

/* The cart drawer owns the right edge, so the usual tuck would slide the
   bubble underneath it and out of reach — the one place a customer is most
   likely to want to ask something ("is this the right box?"). Shift it left
   of the panel instead, where it stays visible and clear of Checkout.
   Returns the panel rect only when there is somewhere to shift into. */
function cartPanelToClear() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || !drawer.classList.contains('active')) return null;
  const inner = drawer.querySelector('.drawer__inner');
  if (!inner) return null;
  const r = inner.getBoundingClientRect();
  /* Closed or mid-slide: the panel parks off-canvas to the right. */
  if (!r.width || r.left >= window.innerWidth - 1) return null;
  /* Full-bleed drawer (mobile) — no room beside it. */
  if (r.left < CART_MIN_CLEAR) return null;
  return r;
}

function chatWrap() {
  /* The widget's own transparent hit area — the box that has to move. */
  const sr = document.querySelector('shopify-chat')?.shadowRoot;
  const wrap = sr?.querySelector('.activator-nudge');
  /* Bail on a left-positioned bubble (Inbox setting): the tuck direction
     and the rest-inset maths below both assume it hugs the right edge. */
  return wrap && wrap.classList.contains('activator-nudge--right') ? wrap : null;
}

function ensureTuckSheet(host) {
  const sr = host.shadowRoot;
  if (!sr) return false;
  if (sr.__unwTuckSheet) return true;
  try {
    const sheet = new CSSStyleSheet();
    /* Animation is a separate class so selfTest() can measure the tuck
       synchronously, before any transition exists to interpolate it. */
    sheet.replaceSync(
      ':host(.unw-chat-anim) .activator-nudge{transition:right .28s cubic-bezier(.22,1,.36,1)}' +
        ':host(.unw-chat-tucked) .activator-nudge{right:var(--unw-chat-tuck-right,-140px)!important}'
    );
    sr.adoptedStyleSheets = [...sr.adoptedStyleSheets, sheet];
    sr.__unwTuckSheet = true;
    return true;
  } catch {
    /* No constructable stylesheets — leave the bubble alone. */
    return false;
  }
}

function applyTuck(host, wrap, on, rightPx) {
  if (on) {
    /* Default (no rightPx): derived from the widget's own width, so it
       clears the edge whatever size the app renders the pill at. A caller
       passing rightPx is shifting it left of a panel instead — same
       property, positive value. */
    const w = Math.ceil(wrap.getBoundingClientRect().width) + 8;
    const value = typeof rightPx === 'number' ? rightPx : -w;
    host.style.setProperty('--unw-chat-tuck-right', value + 'px');
  }
  host.classList.toggle('unw-chat-tucked', on);
  chatTuck.tucked = on;
  /* The pull-back arrow only makes sense once the bubble has actually left
     the screen. Shifted beside the cart panel it is still there to tap. */
  if (chatTuck.tab) chatTuck.tab.hidden = !on || chatTuck.shifted;
}

function selfTest(host, wrap) {
  /* Captured here and never again: this is the one moment the widget is
     guaranteed to be at rest and not mid-slide. It's the widget's own
     `right` inset (a fixed 20px), so it doesn't change with the viewport
     and must NOT be re-read from a live rect — see syncChatTuck(). */
  chatTuck.restInset = window.innerWidth - wrap.getBoundingClientRect().right;
  const before = wrap.getBoundingClientRect().left;
  applyTuck(host, wrap, true);
  const moved = wrap.getBoundingClientRect().left > before + 1;
  applyTuck(host, wrap, false);
  if (moved) host.classList.add('unw-chat-anim');
  chatTuck.support = moved ? 'ok' : 'unsupported';
  return moved;
}

function ensureTuckTab() {
  if (chatTuck.tab) return chatTuck.tab;
  const tab = document.createElement('button');
  tab.id = 'unw-chat-tab';
  tab.type = 'button';
  tab.hidden = true;
  tab.setAttribute('aria-label', 'Show chat button');
  /* Chevron is decorative — the accessible name lives on the button. */
  tab.innerHTML = '<span aria-hidden="true">‹</span>';
  tab.addEventListener('click', () => {
    chatTuck.manualOut = true;
    chatTuck.manualAt = window.scrollY;
    const host = document.querySelector('shopify-chat');
    const wrap = chatWrap();
    if (host && wrap) applyTuck(host, wrap, false);
  });
  document.body.appendChild(tab);
  chatTuck.tab = tab;
  return tab;
}

function buyActionUnder(box) {
  for (const el of document.querySelectorAll(KEEP_CLEAR_SELECTOR)) {
    if (!el.offsetHeight) continue;
    /* A closed cart drawer still has layout — offsetHeight is non-zero for
       visibility:hidden — and parks its checkout button off-canvas. Only
       dodge buttons a customer can actually reach. `visibility` inherits,
       so testing the button catches the whole hidden panel. */
    if (getComputedStyle(el).visibility === 'hidden') continue;
    const r = el.getBoundingClientRect();
    if (
      r.right > box.left - KEEP_CLEAR_MARGIN &&
      r.left < box.right + KEEP_CLEAR_MARGIN &&
      r.bottom > box.top - KEEP_CLEAR_MARGIN &&
      r.top < box.bottom + KEEP_CLEAR_MARGIN
    ) {
      return el;
    }
  }
  return null;
}

function syncChatTuck() {
  if (chatTuck.support === 'unsupported') return;
  const host = document.querySelector('shopify-chat');
  const wrap = host && chatWrap();
  if (!host || !wrap) return;

  /* An open conversation replaces the activator with the panel, and the
     bar-lift cap fades it out over an expanded drawer. Either way there's
     nothing to dodge with — stand down and let the existing rules hold. */
  const pill = host.shadowRoot.querySelector('.activator');
  const hidden =
    !wrap.getBoundingClientRect().height ||
    (pill && getComputedStyle(pill).opacity === '0') ||
    document.documentElement.classList.contains('bottom-bar-covers-chat');
  if (hidden) {
    if (chatTuck.tucked) applyTuck(host, wrap, false);
    return;
  }

  if (!ensureTuckSheet(host)) {
    chatTuck.support = 'unsupported';
    return;
  }
  if (chatTuck.support === 'untested' && !selfTest(host, wrap)) return;
  ensureTuckTab();

  /* Always decide against the RESTING box, never the live one. Tucked, the
     widget is off-screen and would report "clear" — un-tucking it every
     frame. Worse, `right` is transitioned, so a rect read mid-slide gave a
     resting inset of -93px: a phantom box off the right edge that then
     "collided" with the closed cart drawer forever. Only the width and the
     vertical edges are safe to read live; the tuck never touches those. */
  const r = wrap.getBoundingClientRect();
  const right = window.innerWidth - (chatTuck.restInset ?? 20);
  const box = { left: right - r.width, right, top: r.top, bottom: r.bottom };

  /* A pull-out lapses once the reader moves on, so the bubble goes back to
     protecting buy buttons without them having to put it away. */
  if (chatTuck.manualOut && Math.abs(window.scrollY - chatTuck.manualAt) > MANUAL_OUT_SCROLL) {
    chatTuck.manualOut = false;
  }

  const hit = buyActionUnder(box);
  const want = !!hit && !chatTuck.manualOut;

  /* With the cart drawer open, move aside rather than away. Recomputed every
     pass so a resize while the drawer is open re-lands it against the panel. */
  const panel = want ? cartPanelToClear() : null;
  const shiftRight = panel
    ? Math.round(window.innerWidth - panel.left + CART_CLEAR_GAP)
    : null;

  if (want !== chatTuck.tucked || (want && shiftRight !== chatTuck.shiftRight)) {
    chatTuck.shifted = shiftRight !== null;
    chatTuck.shiftRight = shiftRight;
    applyTuck(host, wrap, want, shiftRight);
  }
}

function watchBottomBars() {
  /* The bars are teleported into <body> by Alpine, so they don't exist at
     DOMContentLoaded — collect() re-runs as they appear. */
  const bars = new Set();
  let queued = false;
  let cartWatched = false;

  function watchCartDrawer() {
    if (cartWatched) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    cartWatched = true;
    /* Two things the existing watchers miss. Opening is an `active` class
       flip on <cart-drawer> itself, which is not one of the bars collected
       above; and the contents (Checkout button included) arrive through the
       Sections API as a deep innerHTML swap, which the body-level childList
       observer cannot see because it is not subtree. Without both, the tuck
       measures a drawer that is still closed or still empty and leaves the
       bubble parked on Checkout. */
    mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
  }

  function collect() {
    for (const bar of document.querySelectorAll(BOTTOM_BAR_SELECTOR)) {
      if (bars.has(bar)) continue;
      bars.add(bar);
      ro.observe(bar);
      mo.observe(bar, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    }
    watchCartDrawer();
    schedule();
  }

  function measure() {
    queued = false;
    measureBars();
    /* Runs on every page, bars or not — the buy buttons it dodges are in
       normal flow, so most of its work is on pages with no bar at all. */
    syncChatTuck();
  }

  function measureBars() {
    /* Nothing to track on pages without a bar — keeps the scroll handler free. */
    if (!bars.size) return;
    let tallest = 0;
    for (const bar of bars) {
      /* offsetHeight is 0 while x-show/x-cloak keeps the bar hidden. */
      if (!bar.offsetHeight) continue;
      const rect = bar.getBoundingClientRect();
      if (rect.bottom < window.innerHeight - 1) continue;
      tallest = Math.max(tallest, Math.min(rect.height, window.innerHeight - rect.top));
    }
    /* Cap the lift so a fully-expanded drawer (75vh) doesn't shove the
       bubble up behind the sticky header — leave room for the ~46px
       activator plus its 20px gap. Past that cap the bubble can't clear
       the bar at all, so fade it out (CSS) until the drawer collapses —
       parked on top it covers the drawer's own close chevron. */
    const header = document.querySelector('.site-header');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const maxLift = Math.max(0, window.innerHeight - headerBottom - 76);
    const lift = Math.round(Math.min(tallest, maxLift));
    document.documentElement.style.setProperty('--bottom-bar-h', lift + 'px');
    document.documentElement.classList.toggle('bottom-bar-covers-chat', tallest > maxLift);

    /* The chat app stacks its own offsets on top of ours — Shopify's theme
       preview bar adds 68px, which lands the bubble in the header. Read the
       activator back (its shadow root is open) and shave any overshoot. Skip
       while it's hidden, e.g. when a conversation is open. */
    const activator = document.querySelector('shopify-chat')?.shadowRoot?.querySelector('.activator');
    if (!activator) return;
    const rect = activator.getBoundingClientRect();
    if (!rect.height) return;
    const overshoot = headerBottom + 8 - rect.top;
    if (overshoot > 1) {
      document.documentElement.style.setProperty('--bottom-bar-h', Math.max(0, Math.round(lift - overshoot)) + 'px');
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measure);
  }

  const ro = new ResizeObserver(schedule);
  /* Alpine toggles these bars with display:none (x-show) and animates the
     drawer open — watch both the box and the attributes that flip it. */
  const mo = new MutationObserver(schedule);
  /* Scroll matters even though the bars are fixed: the PDP bar shows and hides
     on scroll position, and a stale --bottom-bar-h leaves the bubble parked
     over empty space or riding on top of a bar that just came back. */
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  /* The bar lift only ever cared about scrolling, because fixed bars don't
     move when the page reflows. The buy buttons the tuck dodges DO: a
     lazy image resolving, an accordion opening, switching to "One time"
     and collapsing the frequency select all slide them out from under the
     bubble — or under it — with no scroll event to notice. Without these,
     the tuck latches to a stale rect (observed on device: the button had
     moved 60px from where the last measurement left it). */
  window.addEventListener('load', schedule);
  new ResizeObserver(schedule).observe(document.documentElement);
  /* Capture phase, so it lands before the handler that does the reflowing;
     rAF coalescing means the measurement still reads the settled layout. */
  document.addEventListener('click', schedule, true);
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('animationend', schedule, true);
  /* Teleport lands during Alpine.start(); alpine:initialized fires after. */
  document.addEventListener('alpine:initialized', collect);
  new MutationObserver(collect).observe(document.body, { childList: true });
  collect();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchBottomBars);
} else {
  watchBottomBars();
}

/* ---- Global components (loaded on every page) ---- */
Alpine.data('brandHeader', () => ({
  openMenu: null,
  scrolled: false,
  _closeTimer: null,

  init() {
    this._onScroll = () => {
      this.scrolled = window.scrollY > 20;
    };
    /* Close the mobile drawer if the viewport grows past the ≤990px
       breakpoint where the desktop nav takes over (e.g. rotate to
       landscape / resize), so it can't be left stuck open + scroll-locked. */
    this._onResize = () => {
      const nav = Alpine.store('mobileNav');
      if (window.innerWidth > 990 && nav.open) nav.closeNav();
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
    this._onScroll();
  },

  destroy() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    clearTimeout(this._closeTimer);
  },

  openMega(name) {
    clearTimeout(this._closeTimer);
    this.openMenu = name;
  },

  scheduleClose() {
    clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => {
      this.openMenu = null;
    }, 150);
  },

  /* Cancel the pending close. Called on header mouseenter so moving
     from one megamenu back into the header doesn't briefly close
     the dropdown (would flicker has-open-mega off and back). */
  cancelClose() {
    clearTimeout(this._closeTimer);
  },

  openCart(event) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open();
    }
  },
}));

Alpine.data('recipeMatrix', () => ({
  filters: { dietary: [], use: [] },
  query: '',
  filtersOpen: false,
  visible: 0,
  total: 0,

  init() {
    this.total = this.$el.querySelectorAll('.recipe-card').length;
    this.recount();
  },

  toggle(axis, value) {
    const arr = this.filters[axis];
    const i = arr.indexOf(value);
    if (i === -1) arr.push(value);
    else arr.splice(i, 1);
    this.recount();
  },

  clearAll() {
    for (const k of Object.keys(this.filters)) this.filters[k] = [];
    this.query = '';
    this.recount();
  },

  hasAnyFilter() {
    if (this.query.trim().length > 0) return true;
    return Object.values(this.filters).some((arr) => arr.length > 0);
  },

  // Count of active filter selections (chips + an active search),
  // shown as a badge on the mobile "Filters" toggle.
  activeCount() {
    let n = Object.values(this.filters).reduce((sum, arr) => sum + arr.length, 0);
    if (this.query.trim().length > 0) n += 1;
    return n;
  },

  // Called per-card via x-show="matches($el)". The card carries
  // data-tags ("dietary:gluten-free,use:quick-breakfast") and
  // data-search (title + excerpt text). Chip axes: for each axis with
  // a selection, the card must include at least one of those filters
  // (OR within axis); all selected axes must pass (AND across axes).
  // The search query AND's on top, matching title/excerpt text.
  // Reading this.filters and this.query here keeps x-show reactive.
  matches(el) {
    const tags = (el.dataset.tags || '').split(',').map((t) => t.trim());
    for (const axis of Object.keys(this.filters)) {
      const selected = this.filters[axis];
      if (selected.length === 0) continue;
      const hit = selected.some((val) => tags.includes(`${axis}:${val}`));
      if (!hit) return false;
    }
    const q = this.query.trim().toLowerCase();
    if (q) {
      const haystack = (el.dataset.search || '').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  },

  // Count matches straight from the filter logic rather than probing
  // rendered layout (card.offsetParent). offsetParent is null while a
  // card's x-show/x-transition is still settling, which on first paint
  // counted every card as hidden and falsely tripped the empty state.
  // matches() is pure (reads dataset + filters), so this is correct
  // synchronously with no $nextTick timing race.
  recount() {
    const cards = this.$el.querySelectorAll('.recipe-card');
    let n = 0;
    cards.forEach((card) => {
      if (this.matches(card)) n += 1;
    });
    this.visible = n;
  },

  visibleLabel() {
    if (!this.hasAnyFilter()) return `${this.total} recipes`;
    return `${this.visible} of ${this.total} recipes`;
  },
}));

/* News index — the /blogs/news ("Blog") index. Single-axis topic
   filter (type:flavors | events | company | nutrition) plus a free-text
   search, both client-side so the grid stays snappy. Cards carry
   data-tags ("type:flavors,…") and data-search (title + excerpt).
   Registered here (not in section {% javascript %}) so it's available
   the moment Alpine starts — same reason as recipeMatrix above. */
Alpine.data('newsIndex', () => ({
  types: [],
  query: '',
  visible: 0,
  total: 0,

  init() {
    this.total = this.$el.querySelectorAll('.news-card').length;
    this.recount();
  },

  toggle(value) {
    const i = this.types.indexOf(value);
    if (i === -1) this.types.push(value);
    else this.types.splice(i, 1);
    this.recount();
  },

  clearAll() {
    this.types = [];
    this.query = '';
    this.recount();
  },

  hasAnyFilter() {
    return this.types.length > 0 || this.query.trim().length > 0;
  },

  // Per-card via x-show="matches($el)". A selected topic OR-matches the
  // card's type: tags; the search query AND's on top against title/excerpt.
  matches(el) {
    const tags = (el.dataset.tags || '').split(',').map((t) => t.trim());
    if (this.types.length > 0) {
      const hit = this.types.some((val) => tags.includes(`type:${val}`));
      if (!hit) return false;
    }
    const q = this.query.trim().toLowerCase();
    if (q) {
      const haystack = (el.dataset.search || '').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  },

  recount() {
    const cards = this.$el.querySelectorAll('.news-card');
    let n = 0;
    cards.forEach((card) => {
      if (this.matches(card)) n += 1;
    });
    this.visible = n;
  },

  visibleLabel() {
    if (!this.hasAnyFilter()) return `${this.total} posts`;
    return `${this.visible} of ${this.total} posts`;
  },
}));

/* Homepage flavor carousel — JS-driven infinite marquee with manual
   prev/next arrows. Translates the track via transform so we can
   wrap-around invisibly: 5 flavor cards are rendered 3x, the offset
   stays clamped in the middle set (sets 1 and 3 are buffers), so any
   wrap is a frame-perfect jump between identical positions. Pauses
   on hover and on focus-within. Arrow clicks animate offset toward
   targetOffset; auto-marquee resumes when target is reached. */
Alpine.data('flavorCarousel', () => ({
  paused: false,
  offset: 0,
  targetOffset: null,
  setWidth: 0,
  /* Pixels per frame at 60fps ≈ 36px/sec → 5 cards in ~48s. */
  speed: 0.6,
  _rafId: null,
  _ro: null,

  init() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      /* Static: leave transform at translateX(0). The first set of
         cards renders flush with the viewport-left. Arrows still
         work for manual nudge. */
      this.speed = 0;
    }
    this._recompute();
    /* Start at the start of the middle set so we have a full set's
       worth of buffer on either side for invisible wrap-arounds. */
    this.offset = this.setWidth;
    this._apply();
    this._tick = this._tick.bind(this);
    this._rafId = requestAnimationFrame(this._tick);

    this._ro = new ResizeObserver(() => {
      this._recompute();
      while (this.offset >= this.setWidth * 2) this.offset -= this.setWidth;
      while (this.offset < 0) this.offset += this.setWidth;
      if (this.targetOffset !== null) {
        while (this.targetOffset >= this.setWidth * 2) this.targetOffset -= this.setWidth;
        while (this.targetOffset < 0) this.targetOffset += this.setWidth;
      }
      this._apply();
    });
    this._ro.observe(this.$refs.track);

    /* Pause when any descendant gains focus (keyboard nav onto a card
       link). Resume on focusout. Hover pause is wired via @mouseenter
       on the viewport in markup. */
    this.$root.addEventListener('focusin', () => { this.paused = true; });
    this.$root.addEventListener('focusout', () => { this.paused = false; });
  },

  destroy() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    if (this._ro) this._ro.disconnect();
  },

  _recompute() {
    /* Track holds 3 identical sets — scrollWidth / 3 = one set's
       layout width (including each card's trailing margin). */
    this.setWidth = this.$refs.track.scrollWidth / 3;
  },

  _apply() {
    this.$refs.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
  },

  _tick() {
    if (this.targetOffset !== null) {
      const diff = this.targetOffset - this.offset;
      if (Math.abs(diff) < 0.5) {
        this.offset = this.targetOffset;
        this.targetOffset = null;
      } else {
        this.offset += diff * 0.14;
      }
    } else if (!this.paused) {
      this.offset += this.speed;
    }

    while (this.offset >= this.setWidth * 2) {
      this.offset -= this.setWidth;
      if (this.targetOffset !== null) this.targetOffset -= this.setWidth;
    }
    while (this.offset < 0) {
      this.offset += this.setWidth;
      if (this.targetOffset !== null) this.targetOffset += this.setWidth;
    }

    this._apply();
    this._rafId = requestAnimationFrame(this._tick);
  },

  step(dir) {
    const card = this.$refs.track.firstElementChild;
    if (!card) return;
    const cardStep = card.offsetWidth + 24;
    const base = this.targetOffset !== null ? this.targetOffset : this.offset;
    this.targetOffset = base + dir * cardStep;
  },
}));

/* ---- Per-page components (lazy-loaded) ---- */
async function registerIfPresent(selector, importer, name) {
  if (!document.querySelector(selector)) return;
  const mod = await importer();
  Alpine.data(name, mod[name]);
}

Promise.all([
  registerIfPresent('[x-data*="bundleBuilder"]', () => import('./bundle-builder-v3.js'), 'bundleBuilder'),
  registerIfPresent('[x-data*="pdp"]', () => import('./pdp-box-5.js'), 'pdp'),
]).then(() => Alpine.start());
